import axios from "axios";
import { loginResponse } from "@/services/api-schema";
import { KUrls } from "@/helpers/constants";
import { saveAuthToken } from "../../helpers/authHeader";
import i18n from "@/i18n";
import { PASSWORD_CHANGE_RESULT_CODES } from "@/helpers/constants/authConstants";
import {
  RESULT_CODE_PASSWORD_EXPIRED,
  RESULT_CODE_PASSWORD_TEMPORARY,
  RESULT_CODE_PASSWORD_ADMIN_FORCED_CHANGE,
} from "@/helpers/constants/authConstants";

export const authService = {
  login,
  logout,
  getLoginQr,
  listenQrStatus,
  getEasedLoginURL,
  completeEasedLogin,
  processLoginResponse,
};

const authEndpoint = KUrls.AUTHENTICATION_ENDPOINT || "/authenticate";

async function login(username, password) {
  try {
    const response = await axios.post(
      `${KUrls.BASE_REST_URL}${authEndpoint}`,
      { username, password },
      {
        "Content-Type": "application/json",
      }
    );
    const data = await handleResponse(response);

    // Process the login response to handle password change enforcement
    const processedData = processLoginResponse(data);

    // Only save permanent tokens, not temporary ones for password changes
    if (
      processedData[loginResponse.token] &&
      !processedData.requiresPasswordChange
    ) {
      saveAuthToken(processedData[loginResponse.token]);
    }

    if (processedData[loginResponse.userSchemas]) {
      localStorage.setItem(
        "user_schemas",
        JSON.stringify(processedData[loginResponse.userSchemas])
      );
    }

    return processedData;
  } catch (error) {
    // Enhanced error handling for login scenarios
    if (error.response) {
      const errorData = error.response.data;

      // Handle password change requirement - check for passwordStatus object with resultCode
      // Backend returns AuthenticateResponse with passwordStatus when password change is required
      if (
        errorData.passwordStatus &&
        errorData.passwordStatus.resultCode &&
        PASSWORD_CHANGE_RESULT_CODES.includes(
          errorData.passwordStatus.resultCode
        )
      ) {
        // Process the response with passwordStatus
        const processedData = processLoginResponse(errorData);

        // Save the temporary token for password change API call
        if (processedData[loginResponse.token]) {
          saveAuthToken(processedData[loginResponse.token]);
        }

        return processedData;
      }

      // Fallback: Handle password change requirement errors by result code
      if (
        errorData.code &&
        PASSWORD_CHANGE_RESULT_CODES.includes(errorData.code)
      ) {
        // Convert error response to password change requirement
        // IMPORTANT: Include the entire errorData to preserve passwordStatus object
        const processedData = processLoginResponse({
          ...errorData, // Include all fields from backend response
          resultCode: errorData.code,
          username: username,
        });

        // Save the temporary token for password change API call
        if (processedData[loginResponse.token]) {
          saveAuthToken(processedData[loginResponse.token]);
        }

        return processedData;
      }

      // Handle other authentication errors
      // Create structured error object preserving the error code
      const authError = {
        code: errorData.code || -1,
        internalMessage: errorData.internalMessage || "",
        message: errorData.message || "Authentication failed",
      };

      // Log for debugging
      console.error("Authentication error:", authError);

      throw authError;
    }

    // Generic error fallback
    const genericError = {
      code: -1,
      message: "Login failed. Please try again.",
    };

    console.error("Authentication error:", genericError);

    throw genericError;
  }
}

async function getLoginQr(pageUrl, qrSize, qrMime) {
  const response = await axios.get(`${KUrls.BASE_REST_URL}${authEndpoint}/qr`, {
    headers: {
      //   "Content-Type": "application/json",
      // Authorization: authorization,
    },
    params: {
      qrSize: qrSize,
      qrSpecs: null,
      qrType: qrMime,
      pageUrl,
    },
  });
  const data = response.data;
  return data;
}

function listenQrStatus(qrId) {
  const evtSource = new EventSource(
    `${KUrls.DOMAIN}${KUrls.BASE_REST_URL}${authEndpoint}/qr/listen?qrId=${qrId}`
  );
  evtSource.onmessage = (e) => console.log(e);
  return evtSource;
}

function logout() {
  localStorage.removeItem("user");
  localStorage.removeItem("user_schemas");
}

function handleResponse(response) {
  // This function is never actually called in case of error, because of axios interceptor declared in main.js
  const data = response && response.data;
  if (!response.status && response.status != 200) {
    if (response.status === 401) {
      // unauthorized
    }
    const error = (data && data.message) || response.statusText;
    console.log(error);
    return Promise.reject(error);
  }

  return data;
}

async function getEasedLoginURL(pageUrl, redirectPath) {
  const response = await axios.get(`${KUrls.BASE_REST_URL}${authEndpoint}/el`, {
    headers: {},
    params: {
      pageUrl,
      redirectPath,
    },
  });
  const data = response.data;
  return data;
}

async function completeEasedLogin(
  responseBase64,
  signatureBase64,
  signatureAlgorithm
) {
  const response = await axios.get(
    `${KUrls.BASE_REST_URL}${authEndpoint}/el/complete`,
    {
      headers: {},
      params: {
        responseBase64,
        signatureBase64,
        signatureAlgorithm,
      },
    }
  );
  const data = response.data;

  // Process the login response to handle password change enforcement
  const processedData = processLoginResponse(data);

  // Only save permanent tokens, not temporary ones for password changes
  if (
    processedData[loginResponse.token] &&
    !processedData.requiresPasswordChange
  ) {
    saveAuthToken(processedData[loginResponse.token]);
  }

  return processedData;
}

/**
 * Process login response to detect password change requirements
 * @param {Object} data - Raw response data from authentication endpoint
 * @returns {Object} - Processed response with password change flags
 */
function processLoginResponse(data) {
  // Check if the backend PasswordStatus indicates password change is required
  // Check for resultCode since requiresChange is not serialized from Java
  if (
    data.passwordStatus &&
    data.passwordStatus.resultCode &&
    PASSWORD_CHANGE_RESULT_CODES.includes(data.passwordStatus.resultCode)
  ) {
    // Use the specific result code from passwordStatus, not the generic -8000
    const specificResultCode = data.passwordStatus.resultCode;
    return {
      ...data,
      requiresPasswordChange: true, // Add this flag for frontend convenience
      temporaryToken: data[loginResponse.token], // This is a temporary token for password change
      passwordPolicy: data.passwordStatus.policy, // Policy from PasswordStatus
      // Use message based on specific result code from passwordStatus
      passwordChangeMessage: getDefaultPasswordChangeMessage(
        specificResultCode
      ),
      passwordChangeResultCode: specificResultCode, // Use specific code, not generic -8000
    };
  }

  // Fallback: Check for generic password change result code
  // This handles cases where passwordStatus might not be properly serialized
  if (data.resultCode === -8000) {
    // If we have -8000 but no passwordStatus, try to extract from passwordStatus if it exists
    const specificResultCode =
      data.passwordStatus?.resultCode || data.resultCode;
    return {
      ...data,
      requiresPasswordChange: true,
      temporaryToken: data[loginResponse.token],
      passwordChangeMessage: getDefaultPasswordChangeMessage(
        specificResultCode
      ),
      passwordChangeResultCode: specificResultCode,
      passwordPolicy: data.passwordStatus?.policy || data.passwordPolicy,
    };
  }

  // Normal login response
  return data;
}

/**
 * Get default password change message based on result code
 * @param {number} resultCode - Backend result code
 * @returns {string} - User-friendly message
 */
function getDefaultPasswordChangeMessage(resultCode) {
  switch (resultCode) {
    case RESULT_CODE_PASSWORD_EXPIRED: // PASSWORD_EXPIRED
      return i18n.t("password_change.enforcement_messages.expired");
    case RESULT_CODE_PASSWORD_TEMPORARY: // PASSWORD_TEMPORARY
      return i18n.t("password_change.enforcement_messages.temporary");
    case RESULT_CODE_PASSWORD_ADMIN_FORCED_CHANGE: // PASSWORD_ADMIN_FORCED_CHANGE
      return i18n.t("password_change.enforcement_messages.forced");
    default:
      return i18n.t("password_change.enforcement_messages.generic");
  }
}
